// The Company place's words and rules (M11): the read-only banner, the subscription in plain words,
// and which getting-started step comes next. Pure, so the tests can pin the wording.

export interface BillingState {
  readOnly: boolean;
  /** From when the banner warns (7 days before read-only), and when read-only starts. */
  warnFrom: string | null;
  readOnlyFrom: string | null;
  /** The owner can pay; everyone else is told to ask them. */
  canPay: boolean;
}

export interface Subscription {
  status: 'trialing' | 'active' | 'past_due' | 'canceled';
  planName: string;
  priceCents: number;
  currency: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  readOnlyFrom: string | null;
  providerSubscriptionId: string | null;
  canceledAt: string | null;
}

export interface Plan {
  name: string;
  priceCents: number;
  currency: string;
  seatsIncluded: number;
  seatPriceCents: number;
}

export interface Step {
  key: string;
  label: string;
  done: boolean;
  required: boolean;
}

export interface Banner {
  tone: 'warn' | 'bad';
  text: string;
  /** The owner's button (to the subscription), or null for everyone else. */
  action: string | null;
}

const day = (iso: string, timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { month: 'long', day: 'numeric', timeZone }).format(new Date(iso));

export const money = (cents: number, currency: string) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(cents / 100);

export function bannerFor(s: BillingState | null, now: Date, timeZone: string): Banner | null {
  if (!s?.readOnlyFrom) return null;
  if (s.readOnly)
    return {
      tone: 'bad',
      text:
        'Your company is read-only: the subscription is not paid. You can still see everything and download your data.' +
        (s.canPay ? '' : ' Ask the owner to pay.'),
      action: s.canPay ? 'Pay now' : null,
    };
  if (!s.warnFrom || now.getTime() < Date.parse(s.warnFrom)) return null;
  return {
    tone: 'warn',
    text:
      `Your company becomes read-only on ${day(s.readOnlyFrom, timeZone)} unless the subscription is paid.` +
      (s.canPay ? '' : ' Ask the owner to pay.'),
    action: s.canPay ? 'Add a card' : null,
  };
}

export function subscriptionLine(sub: Subscription, timeZone: string, now = new Date()): string {
  const when = (iso: string | null) => (iso ? day(iso, timeZone) : 'soon');
  switch (sub.status) {
    case 'trialing':
      // Past its end the trial is over, whatever the status still says (QA-072).
      if (sub.trialEndsAt && now.getTime() >= Date.parse(sub.trialEndsAt))
        return `Your free trial ended on ${when(sub.trialEndsAt)}. ${
          sub.providerSubscriptionId
            ? 'Your card is charged for the next month.'
            : 'Add a card to keep going.'
        }`;
      return `Free trial until ${when(sub.trialEndsAt)}. ${
        sub.providerSubscriptionId ? 'Your card is charged then.' : 'Add a card to keep going after it.'
      }`;
    case 'active':
      return `Paid to ${when(sub.currentPeriodEnd)}.`;
    case 'past_due':
      return `The last payment did not go through. Pay by ${when(sub.readOnlyFrom)} to keep working.`;
    case 'canceled':
      return `Canceled. Your company becomes read-only on ${when(sub.readOnlyFrom)}.`;
  }
}

/** The first step left, in the list's order (prices and technicians come before the first job), skipping optional ones the person skipped; null once nothing is left. */
export function nextStep(steps: readonly Step[], skipped: ReadonlySet<string> = new Set()): Step | null {
  return steps.find((s) => !s.done && !(s.required === false && skipped.has(s.key))) ?? null;
}

/**
 * The province a company's time zone points to, for the starter's sales tax (QA-331): never Alberta
 * for Vancouver. A zone it doesn't know starts on the list's first, for the owner to change.
 */
export function provinceFromTimeZone(timeZone: string): string {
  const z = timeZone.replace(/^Canada\//, 'America/');
  if (/Vancouver|Pacific|Victoria/.test(z)) return 'BC';
  if (/Edmonton|Mountain|Calgary/.test(z)) return 'AB';
  if (/Regina|Saskatchewan|Swift_Current/.test(z)) return 'SK';
  if (/Winnipeg|Central/.test(z)) return 'MB';
  if (/Toronto|Eastern|Ottawa|Thunder_Bay|Nipigon/.test(z)) return 'ON';
  if (/Montreal|Quebec/.test(z)) return 'QC';
  if (/Moncton/.test(z)) return 'NB';
  if (/Halifax|Atlantic|Glace_Bay/.test(z)) return 'NS';
  if (/St_Johns|Newfoundland/.test(z)) return 'NL';
  if (/Whitehorse|Yukon|Dawson/.test(z)) return 'YT';
  if (/Yellowknife|Inuvik/.test(z)) return 'NT';
  if (/Iqaluit|Rankin|Cambridge_Bay/.test(z)) return 'NU';
  return 'AB';
}
